/**
 * NotificationItem 组件
 * 单个通知项组件
 */

import { memo } from 'react'
import { CheckCircle, AlertCircle, Clock, Info, RotateCcw, Loader2 } from 'lucide-react'
import type { Notification } from './types'

interface NotificationItemProps {
  notification: Notification
  onRetry?: (notificationId: string) => void
  retryingId?: string | null
  onClick?: (notification: Notification) => void
}

// 格式化时间
const formatTime = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)

  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
}

// 获取通知图标
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
    case 'extraction_completed':
      return <CheckCircle className="w-5 h-5 text-green-500" />
    case 'error':
    case 'extraction_failed':
      return <AlertCircle className="w-5 h-5 text-red-500" />
    case 'warning':
    case 'extraction_delayed':
      return <Clock className="w-5 h-5 text-yellow-500" />
    case 'info':
    default:
      return <Info className="w-5 h-5 text-blue-500" />
  }
}

// 判断是否显示重试按钮
const shouldShowRetry = (type: string) => {
  return type === 'error' || 
         type === 'extraction_failed' || 
         type === 'warning' || 
         type === 'extraction_delayed'
}

export const NotificationItem = memo(({
  notification,
  onRetry,
  retryingId,
  onClick,
}: NotificationItemProps) => {
  const isRetrying = retryingId === notification.id
  const showRetry = shouldShowRetry(notification.type)

  return (
    <div
      onClick={() => onClick?.(notification)}
      className={`px-4 py-3 cursor-pointer transition-colors hover:opacity-90 border-b border-slate-800 ${
        notification.read ? '' : 'bg-blue-500/8'
      }`}
    >
      <div className="flex gap-3">
        {/* 图标 */}
        <div className="flex-shrink-0 mt-0.5">
          {getNotificationIcon(notification.type)}
        </div>
        
        {/* 内容 */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-100">
            {notification.title}
          </div>
          {notification.message && (
            <div className="text-xs mt-0.5 text-slate-400">
              {notification.message}
            </div>
          )}
          {notification.detail && (
            <div className="text-xs mt-0.5 truncate text-slate-400">
              {notification.detail}
            </div>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs text-slate-400">
              {formatTime(notification.timestamp)}
            </span>
            
            {/* 重试按钮 */}
            {showRetry && onRetry && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRetry(notification.id)
                }}
                disabled={isRetrying}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all hover:scale-105 bg-yellow-500/20 text-yellow-400 disabled:opacity-50"
              >
                {isRetrying ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RotateCcw className="w-3 h-3" />
                )}
                <span>重试</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})

NotificationItem.displayName = 'NotificationItem'

